// Whole days from the reader's today to a calendar date.
//
// # WHY THIS IS ONE FUNCTION
//
// A due date reaches the console as a calendar date with no clock and no zone:
// 2026-10-12, a date column as the API prints it. Whether it has passed is a
// question about the day where the reader is, and how late it is is a count of
// calendar days there. A thing due on the 11th is late from the first moment of
// the 12th on the reader's own clock, in every zone, and nothing about it
// changes at 05:30 in India, which is only midnight in UTC.
//
// Two places worked this out separately. daysUntil (lib/api.ts) read the date
// as midnight in UTC, read today as midnight where the reader is, and rounded
// the difference. The rounding hid the reader's offset while it was under
// twelve hours, so it was right in India and a day out, all day, from UTC+12
// eastwards. whenWords (views/MyWork.tsx) counted on the reader's calendar and
// then let the API's flag overrule the count, and the flag is set on the day in
// UTC, so a reader west of UTC was told "0 days late" in their evening.
//
// # WHY DAY NUMBERS
//
// A calendar date is turned into the number of its day and the two numbers are
// subtracted. There is no clock in either, so there is no hour to round away
// and no daylight saving change to be an hour out by.
//
// The clock is a parameter so a test can say what time it is. The zone is the
// process's own, as it is the browser's for a reader.

const DAY_MS = 86400000

function dayNumber(year: number, month: number, day: number): number {
  return Date.UTC(year, month - 1, day) / DAY_MS
}

// A calendar date as its day number. Anything else that parses is an instant,
// and counts as the day it falls on where the reader is.
function dayNumberOf(value: string): number | null {
  const date = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (date) return dayNumber(Number(date[1]), Number(date[2]), Number(date[3]))
  const at = new Date(value)
  return Number.isNaN(at.getTime()) ? null : dayNumber(at.getFullYear(), at.getMonth() + 1, at.getDate())
}

// 0 on the day itself, 1 the day before, -1 from the start of the day after.
export function daysUntil(date: string | undefined | null, now: Date = new Date()): number | null {
  if (!date) return null
  const due = dayNumberOf(date)
  return due == null ? null : due - dayNumber(now.getFullYear(), now.getMonth() + 1, now.getDate())
}
